{
  "title": "Onboarding",
  "subtitle": "Each step is gated because the thing being switched on is a customer's outbound mail. This page re-reads the tenant every few seconds, so work done elsewhere shows up here.",
  "loadError": "Could not load onboarding state",
  "badPayload": "The API returned an onboarding payload this portal cannot render.",
  "offboardingTitle": "{{name}} is being offboarded",
  "offboardingBody": "Directory sync, rendering and signature pushes have stopped for this tenant. Onboarding steps do not apply to a tenant in this state.",
  "continue": "Continue",
  "continueTo": "Continue to {{step}}",
  "attest": "Attest",
  "openTester": "Open the configuration tester",
  "requirements": "Requirements",
  "met": "Met",
  "notMet": "Not met",
  "stampingSecret": "Stamping secret",
  "rotateSecret": "Rotate secret",
  "copySecret": "Copy secret",
  "cloudflare": "Cloudflare",
  "connectCloudflare": "Connect Cloudflare",
  "verificationMailbox": "Verification mailbox",
  "acceptedDomain": "Accepted domain",
  "mailFlow": "Mail flow",
  "orgWide": "Org-wide promotion",
  "promote": "Promote org-wide",
  "runAutomation": "Run automation",
  "pasteReport": "Paste report",
  "scriptCopied": "Script copied.",
  "directory": "Directory",
  "connectDirectory": "Connect directory",
  "titleNamed": "Onboarding {{name}}",
  "current": "Current",
  "reached": "reached",
  "attested": "attested",
  "validationEvidence": "validation evidence",
  "needsPerson": "Needs a person",
  "confirmThis": "Confirm this",
  "attestIntro": "This is recorded against your name and the time you confirm it. The platform cannot verify it independently, so the record is the evidence.",
  "attestNoteLabel": "What did you see?",
  "attestNotePlaceholder": "Script reported three objects created; -WhatIf output matched.",
  "attestFailedTitle": "Could not record the confirmation",
  "fullyOnboardedTitle": "Fully onboarded",
  "fullyOnboardedBody": "Every internal sender is stamped. Make sure the customer's service desk knows that \"my signature looks wrong\" is now a ticket for us.",
  "advanceFailedTitle": "The tenant was not moved",
  "stepNotFinishedTitle": "This step is not finished",
  "advancePermissionHint": "Tenant administrators and above can move a tenant through onboarding.",
  "advanceAttestation": "Advanced to {{step}} from the portal.",
  "unknown": "unknown",
  "objectLine": "{{label}}: {{state}}",
  "objectLineUnknown": "{{label}}: unknown",
  "stamping": {
    "storedTitle": "Stamping secret stored",
    "storedBody": "Issued {{when}}. The platform keeps an encrypted copy for mail-flow automation (Create connectors / rule). Plaintext is not shown again — if you need it for Advanced PowerShell or a password manager, rotate the secret.",
    "intro": "The stamping secret is how the platform recognises this tenant's mail. It goes into the transport rule as the second half of the X-OnBrandMail-Auth header. We store an encrypted copy for Create mail flow; plaintext is also shown once so you can put it in a password manager if you want.",
    "issue": "Issue stamping secret",
    "reissue": "Re-issue stamping secret",
    "issuePermissionHint": "Tenant administrators and above can issue the secret.",
    "unconfirmedHint": "A secret was issued {{when}} but nobody confirmed storing it. Re-issuing invalidates that one immediately, which stops this tenant's mail being attributed until the transport rule is updated.",
    "issueFailedTitle": "Could not issue the secret",
    "copyPromptTitle": "Copy this if you need it elsewhere. Create mail flow will use the stored copy.",
    "copyPromptBody": "The platform keeps an encrypted copy for automation and a hash for mail attribution. Plaintext will not be shown again after you hide it — rotate if you need another copy for Advanced PowerShell or a password manager.",
    "ackLabel": "I have noted this secret (password manager / Advanced script) if I need a plaintext copy",
    "confirmFailedTitle": "Could not record the confirmation",
    "doneHide": "Done, hide the secret",
    "reissuedTitle": "Stamping secret re-issued",
    "reissuedBody": "Press Create mail flow again. The new sealed copy will be used — no paste required.",
    "needsReissueTitle": "Stamping secret needs to be re-issued",
    "needsReissueBody": "Create mail flow could not unseal the stored secret (this is the value stamped on the transport rule). Re-issue it here, then press Create mail flow again — no paste required.",
    "reissueFailed": "Could not re-issue the secret.",
    "onFileTitle": "Stamping secret on file",
    "onFileBody": "{{action}} will use the encrypted secret stored when it was issued. Paste below only if you need to override or backfill an older tenant.",
    "label": "Stamping secret",
    "labelOverride": "Stamping secret (optional override)",
    "descOnFile": "Leave blank to use the sealed copy on file.",
    "descNeeded": "Re-issue the secret on this tenant (so the platform keeps an encrypted copy), or paste the value issued earlier."
  },
  "cf": {
    "heading": "Customer DNS",
    "introCloudflare": "Cert subject and M365 accepted domain is {{fqdn}}. This apex is on Cloudflare — connect so we can publish the SPF include and Microsoft verification TXT after you accept the grant.",
    "introManual": "Cert subject and M365 accepted domain is {{fqdn}}. This domain is not on Cloudflare. Publish the records below at the DNS host, then press Test.",
    "introSimpleCloudflare": "Connect Cloudflare so we can publish DNS for {{fqdn}}.",
    "introSimpleManual": "Enter the customer's sending domain, then publish DNS for {{fqdn}} or press Test if the records are already there.",
    "advanced": "Advanced DNS details",
    "hideAdvanced": "Hide Advanced",
    "connectedTitle": "Cloudflare connected",
    "connectedFlash": "Cloudflare connected. DNS can be applied for this tenant.",
    "connectDenied": "Cloudflare authorisation was not granted.",
    "zone": "Zone {{name}}",
    "tokenPrefix": "token {{prefix}}",
    "lastDnsWrite": "last DNS write {{when}}",
    "apexLabel": "Customer apex",
    "apexDescription": "Public sending domain that hosts DNS (e.g. trucell.com.au)",
    "apexPlaceholder": "customer.com.au",
    "connect": "Connect to Cloudflare",
    "connectedMessage": "Connected Cloudflare zone for {{apex}}",
    "connectFailed": "Cloudflare connect failed",
    "applyDns": "Apply SPF + verification DNS",
    "dnsApplied": "DNS applied on {{apex}} for {{fqdn}}",
    "dnsFailed": "DNS apply failed",
    "disconnected": "Cloudflare disconnected for this tenant",
    "disconnect": "Disconnect",
    "permissionHint": "Tenant administrators and above can update customer DNS.",
    "providerCloudflare": "Cloudflare",
    "providerOther": "Other DNS host",
    "providerUnknown": "DNS host unknown",
    "recordsHeading": "Suggested DNS records",
    "recordType": "Type",
    "recordName": "Name",
    "recordValue": "Value",
    "recordStatus": "Live",
    "recordPresent": "Present",
    "recordMissing": "Missing",
    "recordPending": "After accepted domain",
    "test": "Test DNS",
    "testPassedTitle": "Public DNS has the required records",
    "testPassedBody": "The include and verification TXT (when issued) are visible in public DNS.",
    "testPassedVerified": "{{fqdn}} is verified in Microsoft 365.",
    "testMissing": "One or more required records are not visible in public DNS yet.",
    "testFailed": "DNS test failed",
    "probeFailed": "Could not look up public DNS",
    "oauthNotConfigured": "Connect to Cloudflare is not configured on this deployment. Use a Zone:DNS:Edit API token below, publish the records manually, or ask an operator to set Cloudflare OAuth.",
    "tokenLabel": "Cloudflare API token",
    "tokenDescription": "Create a token with Zone:DNS:Edit for this zone. Prefer this over OAuth — access tokens from Connect expire and we do not refresh them.",
    "tokenPlaceholder": "Paste Zone:DNS:Edit token",
    "connectWithToken": "Save API token",
    "authExpiredTitle": "Cloudflare credentials expired",
    "authExpiredBody": "Apply DNS failed with an authentication error. OAuth access tokens expire; disconnect if needed, then paste a Zone:DNS:Edit API token and save it.",
    "issueCert": "Issue relay certificate",
    "issueCertOk": "Relay certificate issued for {{fqdn}} ({{thumbprint}}).",
    "issueCertFailed": "Could not issue the relay certificate",
    "certPresentTitle": "Relay certificate on disk",
    "certPresentBody": "{{fqdn}} has thumbprint {{thumbprint}}."
  },
  "domain": {
    "verifiedTitle": "Accepted domain verified",
    "verifiedBody": "{{fqdn}} is verified in this Microsoft 365 tenant. Relay attribution for NDRs and out-of-office can use it.",
    "intro": "We add and verify {{fqdn}} in Microsoft 365 with the connector app. The verification TXT is published automatically when Cloudflare is connected, or shown above for you to add at the DNS host.",
    "needCloudflare": "Connect to Cloudflare on this tenant first so we can publish the MS= TXT. After that, this button is the only remaining step.",
    "needManualDns": "After you add the accepted domain, publish the MS= TXT shown above, then press Test DNS.",
    "add": "Add accepted domain",
    "verifiedNotifyTitle": "Accepted domain verified",
    "verifiedNotifyBody": "{{fqdn}} is now an accepted domain. Exchange may take a minute to list it — run Preflight again.",
    "notVerifiedTitle": "Accepted domain not verified yet",
    "statusFallback": "Status is {{status}}. Publish the DNS records above, then press Test DNS.",
    "addFailedTitle": "Could not add accepted domain",
    "requestFailed": "The request failed."
  },
  "verify": {
    "passedTitle": "Verification mailbox confirmed the stamp",
    "failedTitle": "Last send did not pass",
    "waitingTitle": "Send a test message",
    "passedBody": "This step is confirmed. Another send is not required unless you want a fresh observation.",
    "failedBody": "The mailbox received a message, but it was not stamped. A bounce from smtp.onbrandmail.com never reaches this mailbox — that is the stamper rejecting the send, not a failed stamp check. Fix mail flow, then send again; a pass confirms this step automatically.",
    "waitingBody": "Have a pilot user send any ordinary message to this address. The platform receives it through Resend and checks that the signature was inserted and that X-OnBrandMail-Processed is present.",
    "copyAddress": "Copy address",
    "showBypass": "Advanced — bypass without canary",
    "hideBypass": "Hide Advanced",
    "bypassHint": "Only when a stamped send to this mailbox cannot be completed (blocked outbound, verification receive broken, or an agreed exception). This unlocks Validated without machine proof of the full path.",
    "bypassButton": "Bypass verification mailbox",
    "bypassTitle": "Bypass verification mailbox",
    "bypassIntro": "This is recorded against your name. Prefer fixing mail flow and sending the canary. Use bypass only with a written reason a reviewer can audit later.",
    "bypassReasonLabel": "Why is the canary not possible?",
    "bypassReasonDescription": "At least 20 characters. Name what you checked and the change or ticket reference.",
    "bypassReasonPlaceholder": "Verification receive down after Resend outage; Preflight + automated validation green; CHG-1234 approved skip.",
    "bypassConfirm": "Record bypass"
  },
  "validation": {
    "run": "Run automated validation",
    "hint": "Issues the relay certificate when possible, then runs Exchange validation with the connector app and files the result — no PowerShell paste required.",
    "relayCertTitle": "Relay certificate",
    "couldNotStart": "Could not start",
    "startFailed": "Could not start automated validation.",
    "finishedFindings": "Exchange validation finished with findings: {{error}}",
    "finishedOk": "Automated validation finished. Evidence is on file — refresh onboarding if Continue is still disabled.",
    "finishedFailed": "Automated validation failed. Check the Tester history for Exchange findings."
  },
  "promotePanel": {
    "title": "Promote org-wide",
    "body": "This clears the pilot scope and enables the transport rule for everyone. From the moment it takes effect, every message this organisation sends is routed out through OnBrand Mail and back again. If the platform is unreachable, Exchange holds their mail and retries rather than delivering it unstamped — delayed mail, not lost mail, but a customer-affecting incident either way.",
    "watchHint": "Do this inside the agreed change window, and stay with it for thirty minutes afterwards: stamp rate should rise to the tenant's normal outbound volume while queue depth and oldest-message age stay flat.",
    "staleHint": "Older than a week. Re-run the configuration tester before promoting.",
    "notReadyTitle": "Not ready to promote",
    "changeRefLabel": "Change reference",
    "changeRefDescription": "Recorded against the promotion in Exchange and in this tenant's onboarding evidence.",
    "changeRefPlaceholder": "CHG-1234",
    "ackLabel": "I acknowledge that every message {{name}} sends will be routed through this platform",
    "promotingTitle": "Promoting in Exchange",
    "promotingBody": "Clearing the pilot scope on the transport rule. This page updates when Exchange finishes.",
    "failedTitle": "Promote org-wide failed",
    "failedFallback": "The promotion job failed.",
    "notStartedTitle": "Promotion was not started",
    "button": "Promote org-wide",
    "permissionHint": "Tenant administrators and above can promote a tenant.",
    "needAck": "Tick the acknowledgement to enable this.",
    "needBoth": "A change reference and the acknowledgement are both required.",
    "showAdvanced": "Advanced — break-glass script",
    "hideAdvanced": "Hide Advanced",
    "scriptTitle": "Promote-TenantMailFlow.ps1",
    "scriptDescription": "Only if automation cannot reach Exchange. Run with -WhatIf first. The script re-runs the validation gate itself and refuses to proceed if anything fails."
  },
  "flow": {
    "repairIntro": "If someone deleted an OnBrandMail connector or the transport rule in Exchange, run Preflight to see what is missing, then recreate. Recreate also enables a disabled stamping rule. Remove leftover or wrong connectors first, then recreate.",
    "createIntro": "We create the three OnBrandMail Exchange objects with app-only access (same connector app as directory sync) and enable the stamping rule. This needs Exchange.ManageAsApp plus Exchange Administrator on the connector — powerful permissions; consent already covered them when the directory was connected. Remove leftovers first if Preflight lists competing or wrong connectors.",
    "createIntroSimple": "We'll set up mail routing. You can limit it to a pilot group. Customer mail does not change until you continue.",
    "repairIntroSimple": "Mail routing is already in place. Run Preflight to verify Exchange still has the connectors. Open Advanced only for remove, WhatIf, or PowerShell.",
    "repairIntroNeedsRepair": "Preflight says OnBrandMail objects are missing in Exchange. Recreate them below, or open Advanced for remove and PowerShell.",
    "advanced": "Advanced",
    "hideAdvanced": "Hide Advanced",
    "preflightClearBody": "Mail routing looks healthy.",
    "preflightAutoFixBody": "Press Auto-fix to create missing OnBrandMail objects and disable competing signature redirect rules (for example Black Pearl). Leftover signature outbound connectors are removed too.",
    "preflightIssuesBody": "Review the connector state below. Recreate missing objects, or open Advanced to remove leftovers and run PowerShell.",
    "preflightSeeAdvanced": "Review the connector state below. Recreate missing objects, or open Advanced to remove leftovers and run PowerShell.",
    "autoFix": "Auto-fix",
    "autoFixWhatIf": "Auto-fix (WhatIf)",
    "autoFixHint": "Creates outbound, inbound, and the transport rule if missing; disables competing signature redirect rules; removes leftover signature outbound connectors.",
    "autoFixStartFailed": "Auto-fix failed to start.",
    "notEnabledTitle": "Mail-flow automation not enabled yet",
    "notEnabledBody": "Exchange Administrator has not been assigned to the connector in this customer tenant.",
    "enable": "Enable mail-flow automation",
    "enableFailed": "Enable failed.",
    "enableSucceeded": "Exchange Administrator is assigned. You can run Preflight now.",
    "enableStillBlocked": "Exchange Administrator is still not assigned. See the message above.",
    "reconsentHint": "If directory sync already works, the customer may need to run Admin consent again so RoleManagement.ReadWrite.Directory and Exchange.ManageAsApp are granted in their tenant.",
    "graphConnectorHint": "This deployment is missing Graph connector credentials (GRAPH_CONNECTOR_CLIENT_ID and graph-connector.pfx). Fix platform config before onboarding customers.",
    "preflightNeedsEnable": "Run Enable mail-flow automation first. Preflight needs Exchange Administrator on the connector app.",
    "checking": "Checking connectors…",
    "preflight": "Preflight",
    "removeConnectors": "Remove connectors",
    "removeNothingPresent": "Nothing to remove — Preflight did not find OnBrandMail connectors or the transport rule.",
    "showRepair": "Show repair",
    "hideRepair": "Hide repair",
    "couldNotStart": "Could not start",
    "preflightHardFails": "Preflight found hard failures",
    "preflightWarnings": "Preflight found warnings",
    "preflightObjectsMissing": "Preflight found missing connectors",
    "preflightClear": "Preflight looks clear",
    "lastRun": "Last run {{when}}",
    "outbound": "Outbound",
    "inbound": "Inbound",
    "rule": "Rule",
    "preflightStartFailed": "Preflight failed to start.",
    "createMailFlow": "Create mail flow",
    "recreateMissing": "Recreate missing objects",
    "createWhatIf": "Create (WhatIf)",
    "recreateWhatIf": "Recreate (WhatIf)",
    "whatIfStartFailed": "WhatIf failed to start.",
    "applyStartFailed": "Apply failed to start.",
    "applyDespiteWarnings": "Apply despite warnings",
    "advancedPs": "Advanced: download PowerShell (break-glass / GDAP)",
    "newScriptTitle": "New-TenantMailFlow.ps1",
    "newScriptDescription": "Use only when automation cannot run (GDAP break-glass, or Exchange role assignment blocked). Run with -WhatIf first. The transport rule is created enabled.",
    "removeScriptTitle": "Remove-TenantMailFlow.ps1",
    "removeScriptDescription": "Break-glass offboarding with a drain wait. Portal Remove skips the drain — use this script when mail is already flowing through OnBrandMail.",
    "removeModalTitle": "Remove Exchange objects",
    "previewStartFailed": "Preview failed to start.",
    "removeStartFailed": "Remove failed to start.",
    "pilotGroup": "Pilot group",
    "pilotGroupNone": "Directory is synced but none of the groups have an email, so Exchange cannot use them. Leave empty for the whole tenancy.",
    "pilotGroupHint": "Leave empty for the whole tenancy. Search the mail-enabled groups and distribution lists already synced from the directory.",
    "loadingGroups": "Loading groups…",
    "wholeTenancy": "Whole tenancy",
    "noGroupMatch": "No mail-enabled group matches that search.",
    "jobFinished": "Mail-flow job finished.",
    "preflightFinished": "Preflight finished. Review the findings below.",
    "previewFinished": "Preview finished. Nothing was deleted. Allow this step to run it for real.",
    "removeStepFinished": "{{step}} finished. Allow the next step when you are ready.",
    "removalFinished": "Removal finished. Run Preflight, then recreate if you still need the objects.",
    "repairFinished": "Mail flow repair finished. Missing objects were recreated; existing ones were left unchanged. Run Preflight to confirm Exchange — the previous scan is out of date.",
    "applyFinished": "Mail flow apply finished. Run Preflight to confirm Exchange.",
    "validationFinished": "Validation finished.",
    "validationFinishedFindings": "Validation finished with findings — open the Pilot card or Tester for details.",
    "promotionFinished": "Promotion finished. The transport rule is now org-wide.",
    "jobGeneric": "Mail-flow job",
    "kindPreflight": "Preflight",
    "kindRemove": "Remove connectors",
    "kindRemoveStep": "Remove {{step}}",
    "kindApply": "Create mail flow",
    "kindValidate": "Validation",
    "kindPromote": "Promote org-wide",
    "failedTitle": "{{kind}} failed",
    "whatIfFinishedTitle": "{{kind}} (WhatIf) finished",
    "finishedTitle": "{{kind}} finished",
    "jobFailedDetail": "The mail-flow job failed. Details are below.",
    "jobFailedNoDetail": "The mail-flow job failed with no further detail.",
    "progressQueued": "{{kind}} queued…",
    "progressRunning": "{{kind}} running…",
    "jobWaitBody": "Exchange Online jobs often take 30–90 seconds. This panel will show what happened when it finishes — do not refresh yet.",
    "removeWizardIntro": "Each step asks before it changes Exchange. Skip a step to leave those objects alone. This does not wait for in-flight mail to drain — use Advanced Remove-TenantMailFlow.ps1 if this tenant is already stamping.",
    "removeAllDoneTitle": "All remove steps have been allowed or skipped",
    "removeAllDoneBody": "Run Preflight to confirm Exchange is clean, then recreate if you still need the objects.",
    "lastStepFailed": "Last step failed",
    "lastStepResult": "Last step result",
    "skipStep": "Skip this step",
    "previewStep": "Preview this step",
    "allowAndRun": "Allow and run",
    "stepRules": "Transport rules",
    "stepOutbound": "Outbound connector",
    "stepInbound": "Inbound connector",
    "stepLeftovers": "Leftover connectors",
    "askRulesTitle": "Allow removal of transport rules?",
    "askRulesBody": "Exchange will not delete a connector while a rule still routes to it. This disables and deletes {{name}}{{state}} and any other rule still pointing at the OnBrandMail outbound connector.",
    "askRulesDefaultName": "OnBrandMail Signature Stamping",
    "otherRulesTitle": "Other rules Preflight listed",
    "askOutboundTitle": "Allow removal of the outbound connector?",
    "askOutboundBody": "Deletes {{name}}{{state}}. If a rule still points at it, this step waits and retries — Exchange requires the rule to be gone first.",
    "askOutboundDefaultName": "OnBrandMail Outbound to Signature Service",
    "askInboundTitle": "Allow removal of the inbound connector?",
    "askInboundBody": "Deletes {{name}}{{state}}. Return-path mail will fail if anything is still coming back through OnBrandMail.",
    "askInboundDefaultName": "OnBrandMail Inbound from Signature Service",
    "askLeftoversTitle": "Allow removal of leftover signature connectors?",
    "askLeftoversBody": "Deletes other outbound connectors whose name or smart host looks like signature routing, plus any leftover transport rules that still route to them.",
    "leftoversListedTitle": "Preflight listed leftovers",
    "leftoversNone": "Preflight did not list leftovers. This step will still scan Exchange and report if nothing matches.",
    "currentlyState": " (currently {{state}})"
  },
  "dir": {
    "title": "Directory",
    "intro": "Users, groups and merge-field values are mirrored from the customer's directory and read from that copy — never from Microsoft at send time. Signatures re-render automatically when a sync finds a change.",
    "provider": "Provider",
    "directoryTenant": "Directory tenant",
    "usersMirrored": "Users mirrored",
    "noneYet": "None yet",
    "lastSync": "Last sync",
    "syncFailedTitle": "The last directory sync failed",
    "blocksMailFlowTitle": "Directory sync has to succeed first",
    "blocksMailFlowBody": "Consented means we can read the customer's users and groups. The last sync failed, so mail flow is not next. Fix the error on the Directory card above — Preflight uses the same Graph credentials.",
    "connectionWorks": "Connection works",
    "connectionFailed": "Could not reach the directory",
    "connectionOkBody": "{{org}}. Verified domains: {{domains}}.",
    "noneReported": "none reported",
    "syncFinishedTitle": "Sync finished",
    "syncNoChanges": "The local copy already matches the customer's directory — nothing changed.",
    "syncFullLead": "Read the whole directory.",
    "syncIncrementalLead": "Checked for changes since the last sync.",
    "syncPeople": "People: {{detail}}.",
    "syncAdded": "{{count}} added",
    "syncUpdated": "{{count}} updated",
    "syncRemoved": "{{count}} removed",
    "syncGroups_one": "Groups: {{count}} changed.",
    "syncGroups_other": "Groups: {{count}} changed.",
    "syncMemberships_one": "{{count}} group membership change.",
    "syncMemberships_other": "{{count}} group membership changes.",
    "syncSkipped": "Skipped: {{reason}}.",
    "testConnection": "Test connection",
    "syncNow": "Sync now",
    "fullResync": "Full resync",
    "consentHint": "Consent has not been proven yet. Test the connection — it authenticates against the customer's directory and records consent only if Microsoft answers.",
    "step1": "Step 1 — which mail platform does this customer use?",
    "step1Body": "No directory is connected yet. Choose Microsoft 365 or Google Workspace, then sign in as their Global Admin. You can hand them this screen.",
    "microsoft365": "Microsoft 365",
    "googleWorkspace": "Google Workspace",
    "connectPermissionHint": "Tenant administrators and above can connect a directory.",
    "microsoftConsentHint": "A Global Admin for the customer must complete this. You can hand them this screen, or sign in as that Global Admin yourself. Consent grants directory sync plus Exchange mail-flow automation (Exchange.ManageAsApp and assigning Exchange Administrator to our connector in their tenant) — that is powerful; only proceed for a customer who asked for this service.",
    "signInMicrosoft": "Sign in with Microsoft (Global Admin)",
    "consentStartFailed": "Could not start Microsoft admin consent.",
    "consentFailedTitle": "Could not start consent",
    "googleUnavailableTitle": "Google Workspace is not available yet",
    "googleUnavailableBody": "Domain-wide delegation for Google is not enabled in this build. Choose Microsoft 365 for now, or use the advanced path only if support has given you a directory id to record by hand.",
    "advancedManual": "Advanced: enter directory id manually",
    "entraIdLabel": "Microsoft Entra tenant id",
    "entraIdDescription": "Break-glass only. Prefer Sign in with Microsoft above so consent and the id arrive together.",
    "entraIdPlaceholder": "2f1c8a91-0000-4c3d-9f0a-abcdefabcdef",
    "recordedOk": "Directory recorded. Test the connection to confirm consent.",
    "recordFailed": "Could not record the directory.",
    "connect": "Connect directory"
  },
  "fields": {
    "fillBlanksHint": "These values fill blank directory fields for members. Tenant defaults apply only when sync and group fills left the field blank. A personal override still wins.",
    "overrideHint": "Overrides replace the synced value (and any group fill or tenant default) for this person. Clear a field to hand it back to the directory.",
    "emptyPerson": "No merge fields are populated for this person yet.",
    "emptySync": "No merge fields have synced yet.",
    "overridden": "Overridden",
    "current": "Current: {{value}}",
    "synced": " · Synced: {{value}}",
    "fillWhenBlank": "Fill when blank",
    "overrideValue": "Override value",
    "revert": "Revert",
    "showAll": "Show all tenant fields",
    "addField": "Add field",
    "addFieldPlaceholder": "customAttribute",
    "saveOverrides": "Save field overrides"
  },
  "directoryConnected": "Directory connected. You can test the connection and run a sync.",
  "directoryConnectedContinue": "Directory connected. You can continue onboarding once other requirements are met.",
  "adminConsentDenied": "Admin consent was not granted.",
  "directoryConsentFailed": "Directory consent failed.",
  "noAccessTitle": "No access to this tenant",
  "noAccessBody": "You do not have a role on this tenant.",
  "readOnlyTitle": "Read-only",
  "readOnlyBody": "You can follow this onboarding and read every requirement, but moving the tenant between states needs the Tenant administrator role."
}
